/**
 * PlannerTimeline — quarter-hour placement grid (spec §8.3).
 *
 * - Proportional blocks: position and height follow startMinute/duration.
 * - Place-on-tap: each hour row is a ≥44px tap target; the vertical position
 *   inside the row selects the quarter (00/15/30/45). Keyboard Enter places
 *   at the start of the hour — exact times are always adjustable via the
 *   edit sheet (accessible non-drag alternative, no drag required anywhere).
 * - Edit icon sits immediately LEFT of the delete icon (roadmap refinement).
 * - Medication occurrences render as read-only pill-styled markers that deep
 *   link to Gezondheid.
 */
import { Link } from 'react-router-dom'
import { IconButton, SyncStatus } from '@/components/ui'
import { EditIcon, NoteIcon, PillIcon, TrashIcon } from '@/components/icons'
import { PATHS } from '@/app/router/paths'
import { getActivityById } from '@/data/activities'
import { ActivityIcon } from '@/icons/activities'
import type { MedicationOccurrence, PlannerItem, SyncSaveState } from '@/types'
import { formatDuration, formatMinutes, parseTimeToMinutes } from './plannerDates'

export const HOUR_HEIGHT_PX = 56

export type PlannerTimelineProps = {
  items: PlannerItem[]
  occurrences: MedicationOccurrence[]
  syncStates: Record<string, SyncSaveState>
  rangeStartMinute: number
  rangeEndMinute: number
  placementArmed: boolean
  onRequestPlace: (startMinute: number) => void
  onEdit: (item: PlannerItem) => void
  onDelete: (item: PlannerItem) => void
}

const OCCURRENCE_STATUS_LABELS: Partial<Record<MedicationOccurrence['status'], string>> = {
  taken: 'Ingenomen',
  missed: 'Gemist',
  cancelled: 'Geannuleerd',
}

function minuteToPx(minute: number, rangeStartMinute: number): number {
  return ((minute - rangeStartMinute) / 60) * HOUR_HEIGHT_PX
}

export function PlannerTimeline({
  items,
  occurrences,
  syncStates,
  rangeStartMinute,
  rangeEndMinute,
  placementArmed,
  onRequestPlace,
  onEdit,
  onDelete,
}: PlannerTimelineProps) {
  const hours: number[] = []
  for (let minute = rangeStartMinute; minute < rangeEndMinute; minute += 60) {
    hours.push(minute)
  }
  const totalHeight = ((rangeEndMinute - rangeStartMinute) / 60) * HOUR_HEIGHT_PX

  const visibleItems = items.filter(
    (item) =>
      item.startMinute < rangeEndMinute && item.startMinute + item.durationMinutes > rangeStartMinute,
  )

  const handleSlotClick = (hourStart: number, offsetY: number) => {
    const quarter = Math.min(3, Math.max(0, Math.floor((offsetY / HOUR_HEIGHT_PX) * 4)))
    onRequestPlace(hourStart + quarter * 15)
  }

  return (
    <div className="planner-timeline" style={{ minHeight: totalHeight }}>
      {hours.map((hourStart) => {
        const label = formatMinutes(hourStart)
        return (
          <div key={hourStart} className="planner-timeline__hour" style={{ height: HOUR_HEIGHT_PX }}>
            <span className="planner-timeline__hour-label" aria-hidden="true">
              {label}
            </span>
            {placementArmed ? (
              <button
                type="button"
                className="planner-timeline__slot"
                aria-label={`Plaats in het uur vanaf ${label}; waar je tikt in het uurvak bepaalt het kwartier`}
                onClick={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect()
                  handleSlotClick(hourStart, event.clientY - rect.top)
                }}
              />
            ) : (
              <span className="planner-timeline__slot planner-timeline__slot--inactive" aria-hidden="true" />
            )}
          </div>
        )
      })}

      <div className="planner-timeline__overlay">
        {occurrences.map((occurrence) => {
          const minute = parseTimeToMinutes(occurrence.localTime)
          if (minute === null || minute < rangeStartMinute || minute >= rangeEndMinute) return null
          const statusLabel = OCCURRENCE_STATUS_LABELS[occurrence.status]
          return (
            <Link
              key={occurrence.id}
              to={PATHS.app.health}
              className="planner-med"
              style={{ top: Math.max(0, minuteToPx(minute, rangeStartMinute) - 16) }}
              aria-label={`Medicatie om ${occurrence.localTime}${statusLabel ? `, ${statusLabel.toLowerCase()}` : ''}. Open Gezondheid.`}
            >
              <PillIcon size={16} />
              <span className="planner-med__text">Medicatie {occurrence.localTime}</span>
              {statusLabel ? <span className="planner-med__status">{statusLabel}</span> : null}
            </Link>
          )
        })}

        {visibleItems.map((item) => {
          const endMinute = item.startMinute + item.durationMinutes
          const clampedStart = Math.max(item.startMinute, rangeStartMinute)
          const clampedEnd = Math.min(endMinute, rangeEndMinute)
          const top = minuteToPx(clampedStart, rangeStartMinute)
          const proportionalHeight = ((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT_PX
          const height = Math.max(proportionalHeight, 44)
          const activity = getActivityById(item.activityId)
          const syncState = syncStates[item.id] ?? null
          const timeRange = `${formatMinutes(item.startMinute)}–${formatMinutes(endMinute)}`
          return (
            <article
              key={item.id}
              className="planner-item"
              style={{ top, height, borderColor: item.color }}
              aria-label={`${item.activityName}, ${timeRange}${activity?.badge === 'warning' ? '. Let op: deze activiteit kan veel schermtijd met zich meebrengen.' : ''}`}
            >
              <span className="planner-item__accent" style={{ backgroundColor: item.color }} aria-hidden="true" />
              <span className="planner-item__icon" style={{ color: item.color }}>
                <ActivityIcon
                  iconId={item.iconId}
                  size={20}
                  showWarningBadge={activity?.badge === 'warning'}
                />
              </span>
              <div className="planner-item__body">
                <span className="planner-item__name">
                  {item.activityName}
                  {item.notes ? (
                    <span className="planner-item__note" title={item.notes}>
                      <NoteIcon size={14} />
                      <span className="visually-hidden">Heeft een notitie</span>
                    </span>
                  ) : null}
                </span>
                <span className="planner-item__time">
                  {timeRange} · {formatDuration(item.durationMinutes)}
                </span>
                {syncState && syncState !== 'cloud_saved' ? (
                  <SyncStatus state={syncState} className="planner-item__sync" />
                ) : null}
              </div>
              <div className="planner-item__actions">
                <IconButton
                  aria-label={`Bewerk ${item.activityName} om ${formatMinutes(item.startMinute)}`}
                  variant="ghost"
                  onClick={() => onEdit(item)}
                >
                  <EditIcon size={18} />
                </IconButton>
                <IconButton
                  aria-label={`Verwijder ${item.activityName} om ${formatMinutes(item.startMinute)}`}
                  variant="danger"
                  onClick={() => onDelete(item)}
                >
                  <TrashIcon size={18} />
                </IconButton>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}
